import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import apiClient from '../../utils/api';

// The dial's control panel: set the target for one metric right on its card,
// behind the "Adjust target" disclosure so the card stays a glance. This is the
// same MetricTarget the coach and trajectory read from — one place to turn the
// knob. Deliberately shows only the *setting* controls (opt-out, reference
// ranges, custom low/high, reset); the card above already shows where you are.

const fmt = (v, unit) => {
  if (v === null || v === undefined) return '—';
  if (unit === 'min') {
    const h = Math.floor(v / 60);
    const m = Math.round(v % 60);
    return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  }
  const num = Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return unit === '%' || unit === '/10' ? `${num}${unit}` : `${num} ${unit}`;
};

const formatRange = (low, high, unit) => {
  if (low !== null && high !== null) {
    if (unit === 'min') return `${fmt(low, unit)} – ${fmt(high, unit)}`;
    const lo = Number.isInteger(low) ? low.toLocaleString() : low.toLocaleString(undefined, { maximumFractionDigits: 1 });
    return `${lo} – ${fmt(high, unit)}`;
  }
  if (low !== null) return `≥ ${fmt(low, unit)}`;
  if (high !== null) return `≤ ${fmt(high, unit)}`;
  return null;
};

const provenance = (t) => {
  if (t.ultimate_is_custom) {
    return { text: 'set by you', chip: 'bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-300' };
  }
  const tier = t.seed_tier === 'standard' ? 'standard' : 'optimal';
  if (t.seed_source === 'generic') {
    return { text: `${tier} · generic`, chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' };
  }
  return {
    text: t.seed_source === 'personalized' ? `${tier} · tuned to you` : tier,
    chip: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  };
};

const DialTargetEditor = ({ target, onSaved }) => {
  const [low, setLow] = useState(target.ultimate_low ?? '');
  const [high, setHigh] = useState(target.ultimate_high ?? '');
  const [showWhy, setShowWhy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLow(target.ultimate_low ?? '');
    setHigh(target.ultimate_high ?? '');
  }, [target.ultimate_low, target.ultimate_high]);

  const patch = async (body, successMsg) => {
    setSaving(true);
    try {
      const res = await apiClient.patch(`/api/twin/targets/${target.metric_key}`, body);
      onSaved(res.data);
      if (successMsg) toast.success(successMsg);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update target');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post(`/api/twin/targets/${target.metric_key}/reset`);
      onSaved(res.data);
      toast.success(`${target.label} reset to reference range`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset target');
    } finally {
      setSaving(false);
    }
  };

  const prov = provenance(target);
  const targetText = formatRange(target.ultimate_low, target.ultimate_high, target.unit);

  const tierButton = (tier, lowV, highV, citation) => {
    const active = !target.ultimate_is_custom && target.seed_tier === tier;
    return (
      <button
        type="button"
        disabled={saving || active}
        onClick={() => patch({ seed_tier: tier }, `${target.label} now aims at the ${tier} range`)}
        className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
          active
            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200'
            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title={citation}
      >
        <span className="font-semibold capitalize">{tier}</span>{': '}
        <span className="font-semibold">{formatRange(lowV, highV, target.unit) || '—'}</span>
        <span className="block text-sm text-gray-600 dark:text-gray-300">
          {tier === 'optimal' ? 'top-tier health' : 'clinical normal'}{active ? ' · current aim' : ''}
        </span>
      </button>
    );
  };

  return (
    <div className="rounded-xl bg-gray-50 dark:bg-surface-dark-raised p-4 space-y-3">
      {/* On/off — opting out stops tracking + grading this metric */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold uppercase tracking-wider text-green-700 dark:text-green-400">
            Your target
          </p>
          {target.enabled ? (
            <p className="text-base font-bold text-gray-900 dark:text-white">
              {targetText || 'Not set'}
              <span className={`ml-2 align-middle text-sm px-2 py-0.5 rounded-full font-medium ${prov.chip}`}>
                {prov.text}
              </span>
            </p>
          ) : (
            <p className="text-base font-medium text-gray-600 dark:text-gray-300">Not tracking this metric</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => patch({ enabled: !target.enabled })}
          disabled={saving}
          role="switch"
          aria-checked={target.enabled}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            target.enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          title={target.enabled ? 'Tracking on — tap to opt out' : 'Opted out — tap to track'}
        >
          <span
            className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white transition-transform ${
              target.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {target.enabled && (
        <>
          {/* Reference ranges (skip when standard and optimal coincide) */}
          {!target.tiers_coincide && (
            <div className="grid sm:grid-cols-2 gap-2">
              {tierButton('optimal', target.optimal_low, target.optimal_high, target.optimal_citation)}
              {tierButton('standard', target.standard_low, target.standard_high, target.standard_citation)}
            </div>
          )}

          {/* Custom low/high */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-28">
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Low ({target.unit})</label>
              <Input type="number" value={low} onChange={(e) => setLow(e.target.value)}
                     min={target.clamp_low} max={target.clamp_high} step="any" />
            </div>
            <div className="w-28">
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">High ({target.unit})</label>
              <Input type="number" value={high} onChange={(e) => setHigh(e.target.value)}
                     min={target.clamp_low} max={target.clamp_high} step="any" />
            </div>
            <Button
              size="sm"
              disabled={saving}
              onClick={() => patch({
                ultimate_low: low === '' ? null : parseFloat(low),
                ultimate_high: high === '' ? null : parseFloat(high),
              }, `${target.label} target updated`)}
            >
              {saving ? 'Saving…' : 'Save custom'}
            </Button>
            {target.ultimate_is_custom && (
              <Button variant="outline" size="sm" onClick={handleReset} disabled={saving} className="flex items-center">
                <ArrowUturnLeftIcon className="w-4 h-4 mr-1.5" />
                Back to reference
              </Button>
            )}
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300">
            Set low and high to the same number for a single-point target. Your choice always wins —
            out-of-range targets are noted for context, never blocked.
          </p>

          {/* Why these ranges — citations, tucked away */}
          <button
            type="button"
            onClick={() => setShowWhy((s) => !s)}
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-transparent border-none"
          >
            {showWhy ? 'Hide the science' : 'Why these ranges?'}
          </button>
          {showWhy && (
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 bg-white dark:bg-surface-dark-card rounded-md px-3 py-2">
              {target.tiers_coincide ? (
                <p>
                  <span className="font-semibold">{formatRange(target.optimal_low, target.optimal_high, target.unit) || 'No range yet'}</span>
                  {' — '}{target.optimal_citation} <span className="italic">(standard and optimal coincide here)</span>
                </p>
              ) : (
                <>
                  <p>
                    <span className="font-semibold text-green-700 dark:text-green-400">Optimal {formatRange(target.optimal_low, target.optimal_high, target.unit) || '—'}</span>
                    {' — '}{target.optimal_citation}
                  </p>
                  <p>
                    <span className="font-semibold">Standard {formatRange(target.standard_low, target.standard_high, target.unit) || '—'}</span>
                    {' — '}{target.standard_citation}
                  </p>
                </>
              )}
              {target.average_citation && (
                <p>
                  <span className="font-semibold">Average person{target.average_value !== null ? ` ${fmt(target.average_value, target.unit)}` : ''}</span>
                  {' — '}{target.average_citation}
                </p>
              )}
            </div>
          )}

          {target.annotation && (
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md px-3 py-2">
              {target.annotation}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default DialTargetEditor;
